import { Appetite_Model_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useCallback } from 'react';

import { useGetLinkedItems } from '@/hooks/queries/linked-item/useGetLinkedItems';

import { useUnlinkItems } from '../linked-item/useUnlinkItems';
import { useDeleteAppetites } from './useDeleteAppetites';

export type RiskAppetiteRemovalAction = 'delete' | 'unlink' | 'unavailable';

type ClassifyRiskAppetiteRemovalActionArgs = {
  appetiteAggregation: Appetite_Model_Enum;
  riskTier: number | null | undefined;
  enableTierTwoCascading: boolean;
};

export const classifyRiskAppetiteRemovalAction = ({
  appetiteAggregation,
  riskTier,
  enableTierTwoCascading,
}: ClassifyRiskAppetiteRemovalActionArgs): RiskAppetiteRemovalAction => {
  if (appetiteAggregation === Appetite_Model_Enum.Default) {
    return 'delete';
  }

  if (appetiteAggregation !== Appetite_Model_Enum.TopDownCascade) {
    return 'unavailable';
  }

  if (riskTier === 1 || (riskTier === 2 && enableTierTwoCascading)) {
    return 'delete';
  }

  if (riskTier === 3) {
    return 'unlink';
  }

  return 'unavailable';
};

type RiskAppetiteLinkedItem = {
  Id: string;
  RelationshipType?: string | null;
  Source: string;
  Target: string;
};

type ResolveRiskAppetiteLinkIdsArgs = {
  linkedItems: RiskAppetiteLinkedItem[];
  riskId: string;
  appetiteIds: string[];
};

export const resolveRiskAppetiteLinkIds = ({
  linkedItems,
  riskId,
  appetiteIds,
}: ResolveRiskAppetiteLinkIdsArgs): string[] => {
  const requestedAppetiteIds = new Set(appetiteIds);
  const matchingLinks = linkedItems.filter(
    (linkedItem) =>
      linkedItem.RelationshipType === 'parent_child' &&
      linkedItem.Source === riskId &&
      requestedAppetiteIds.has(linkedItem.Target)
  );
  const resolvedAppetiteIds = new Set(
    matchingLinks.map((linkedItem) => linkedItem.Target)
  );
  const missingAppetiteIds = appetiteIds.filter(
    (appetiteId) => !resolvedAppetiteIds.has(appetiteId)
  );

  if (missingAppetiteIds.length > 0) {
    throw new Error(
      `Cannot remove risk appetites because no current-risk relationship was found for: ${missingAppetiteIds.join(
        ', '
      )}`
    );
  }

  return matchingLinks.map((linkedItem) => linkedItem.Id);
};

type UseRemoveRiskAppetitesArgs = {
  riskId: string;
  action: RiskAppetiteRemovalAction;
};

export const useRemoveRiskAppetites = ({
  riskId,
  action,
}: UseRemoveRiskAppetitesArgs) => {
  const { deleteAppetites, loading: deleteLoading } = useDeleteAppetites();
  const { unlinkItems, loading: unlinkLoading } = useUnlinkItems();
  const { data, loading: linkedItemsLoading } = useGetLinkedItems({
    queryArgs: {
      id: riskId,
      includeInternalAudit: false,
      includeCompliance: false,
    },
    shouldSkip: action !== 'unlink',
  });

  const removeAppetites = useCallback(
    async (appetiteIds: string[]): Promise<void> => {
      if (action === 'delete') {
        await deleteAppetites(appetiteIds);

        return;
      }

      if (action === 'unlink') {
        const linkIds = resolveRiskAppetiteLinkIds({
          linkedItems: data?.linked_item ?? [],
          riskId,
          appetiteIds,
        });
        await unlinkItems(linkIds);

        return;
      }

      throw new Error('Risk appetite removal is unavailable for this risk');
    },
    [action, data?.linked_item, deleteAppetites, riskId, unlinkItems]
  );

  return {
    removeAppetites,
    loading:
      action === 'delete'
        ? deleteLoading
        : action === 'unlink'
          ? linkedItemsLoading || unlinkLoading
          : false,
  };
};
